import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../components/Button';
import { useQuiz } from '../contexts/QuizContext';

// Declare fbq type for TypeScript
declare global {
    interface Window {
        fbq: any;
        fbPixelBypass?: {
            isLoaded: () => boolean;
            sendEvent: (eventName: string, eventData: any) => boolean;
            forceBeacon: (eventName: string, eventData: any) => boolean;
        }
    }
}

const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  padding: 1.5rem;
  background-color: #121212;
  color: #fff;
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const QuestionCard = styled(motion.div)`
  background-color: #1e1e1e;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 800px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem;
    border-radius: 12px;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
  width: 100%;
  
  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
  }
`;

const Step = styled.div<{ active: boolean }>`
  width: 14%;
  height: 6px;
  border-radius: 3px;
  background-color: ${props => props.active ? '#4f65f1' : '#333'};
  transition: background-color 0.3s ease;
  
  @media (max-width: 480px) {
    height: 4px;
  }
`;

const QuestionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: #fff;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 1.2rem;
  }
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1.5rem 0 2.5rem;
  width: 100%;
  
  @media (max-width: 480px) {
    gap: 0.75rem;
    margin: 1.2rem 0 2rem;
  }
`;

const OptionButton = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 1rem 1.5rem;
  background-color: ${props => props.selected ? '#2e3f9e' : '#2a2a2a'};
  border: 2px solid ${props => props.selected ? '#4f65f1' : 'transparent'};
  border-radius: 8px;
  color: #fff;
  font-size: 1rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.selected ? '#2e3f9e' : '#333'};
    transform: translateY(-2px);
  }
  
  @media (max-width: 480px) {
    padding: 0.8rem 1.2rem;
    font-size: 0.9rem;
    border-radius: 6px;
  }
`;

const InputField = styled.input`
  width: 100%;
  padding: 1rem 1.5rem;
  background-color: #2a2a2a;
  border: 2px solid transparent;
  border-radius: 8px;
  color: #fff;
  font-size: 1rem;
  margin-bottom: 0.5rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #4f65f1;
  }
  
  @media (max-width: 480px) {
    padding: 0.8rem 1.2rem;
    font-size: 0.9rem;
    border-radius: 6px;
    margin-bottom: 0.5rem;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4d4d;
  font-size: 0.9rem;
  margin: 1rem 0;
  min-height: 1.2rem;
  text-align: center;
  font-weight: 500;
  padding: 0.5rem;
  border-radius: 4px;
  background-color: rgba(255, 77, 77, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin: 0.8rem 0;
    padding: 0.4rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 2rem;
  
  @media (max-width: 480px) {
    margin-top: 1.5rem;
    flex-direction: column;
    gap: 1rem;
  }
`;

// Adicionar componente de loading para feedback visual
const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 4px solid #4f65f1;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Question: React.FC = () => {
    const { questionId } = useParams<{ questionId: string }>();
    const navigate = useNavigate();
    const { answers, updateAnswer, goToNextQuestion, goToPreviousQuestion, isLastQuestion } = useQuiz();
    const [isMobile, setIsMobile] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth <= 480);
        };

        // Verificar na montagem do componente
        checkIfMobile();

        // Adicionar listener para redimensionamento
        window.addEventListener('resize', checkIfMobile);

        // Cleanup
        return () => window.removeEventListener('resize', checkIfMobile);
    }, [questionId]);

    // Verificar se o ID da pergunta é válido e redirecionar se necessário
    useEffect(() => {
        const parsedId = parseInt(questionId || '1');

        // Se o ID não for um número válido ou estiver fora do intervalo 1-6
        if (isNaN(parsedId) || parsedId < 1 || parsedId > 6) {
            navigate('/question/1');
        }
    }, [questionId, navigate]);

    const currentQuestionId = parseInt(questionId || '1');

    // Função para validar email com uma regex mais robusta
    const isValidEmail = (email: string) => {
        // Regex mais robusta para validação de email
        const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return emailRegex.test(email);
    };

    const isValidPhone = (phone: string) => {
        // Verifica se tem pelo menos 14 caracteres com a máscara (00) 0000-0000
        return phone.length >= 14 && phone.replace(/\D/g, '').length >= 10;
    };

    const formatPhoneNumber = (value: string) => {
        // Remove todos os caracteres não numéricos
        const numbersOnly = value.replace(/\D/g, '');

        // Limita a 11 dígitos (DDD + número)
        const limitedNumber = numbersOnly.slice(0, 11);

        // Aplica a máscara
        if (limitedNumber.length <= 2) {
            return limitedNumber;
        } else if (limitedNumber.length <= 6) {
            return `(${limitedNumber.slice(0, 2)}) ${limitedNumber.slice(2)}`;
        } else if (limitedNumber.length <= 10) {
            return `(${limitedNumber.slice(0, 2)}) ${limitedNumber.slice(2, 6)}-${limitedNumber.slice(6)}`;
        } else {
            return `(${limitedNumber.slice(0, 2)}) ${limitedNumber.slice(2, 7)}-${limitedNumber.slice(7)}`;
        }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formattedPhone = formatPhoneNumber(e.target.value);
        updateAnswer('phone', formattedPhone);
        setError('');
    };

    // Função para enviar os eventos do Facebook
    const sendFacebookEvent = (): Promise<boolean> => {
        return new Promise((resolve, reject) => {
            try {
                // Verificar se já enviamos um evento Lead nesta sessão
                const leadSentKey = 'bt_lead_form_sent';
                if (sessionStorage.getItem(leadSentKey) === 'true') {
                    resolve(true);
                    return;
                }

                // Versão mais simples e direta para garantir compatibilidade com o Meta Pixel Helper
                if (typeof window.fbq === 'function') {
                    // Criar um ID único para o lead
                    const leadId = `lead_${Date.now()}`;

                    // Usar uma versão mais completa do evento Lead com dados do usuário
                    window.fbq('track', 'Lead', {
                        content_name: 'Gateway Finder Lead',
                        content_category: 'Comparison',
                        lead_id: leadId,
                        // Adicionar dados do usuário para enriquecer o evento
                        user_email: answers.email || '',
                        billing_volume: answers.billingVolume || '',
                        region: answers.region || '',
                        platform: answers.platform || '',
                        value: 1.00,
                        currency: 'BRL'
                    });

                    // Enviar um segundo evento Lead como backup
                    try {
                        // Verificar se o pixel bypass está disponível
                        if (window.fbPixelBypass && typeof window.fbPixelBypass.forceBeacon === 'function') {
                            window.fbPixelBypass.forceBeacon('Lead', {
                                content_name: 'Gateway Finder Lead',
                                lead_id: leadId
                            });
                        }
                    } catch (e) {
                        // Silently fail
                    }

                    // Marcar que o evento foi enviado
                    sessionStorage.setItem(leadSentKey, 'true');
                    resolve(true);
                } else {
                    // Fallback se fbq não estiver disponível
                    const url = `https://www.facebook.com/tr?id=1206177091200862&ev=Lead&noscript=1&ts=${Date.now()}`;
                    navigator.sendBeacon(url);

                    // Marcar que o evento foi enviado
                    sessionStorage.setItem(leadSentKey, 'true');
                    resolve(true);
                }
            } catch (error) {
                reject(error);
            }
        });
    };

    const getQuestionContent = () => {
        switch (currentQuestionId) {
            case 1:
                return {
                    title: 'Qual é o volume de faturamento mensal?',
                    content: (
                        <OptionsList>
                            {['0 a 10k', '10k a 100k', '100k a 1M', '1M a 10M', '+10M'].map((option) => (
                                <OptionButton
                                    key={option}
                                    selected={answers.billingVolume === option}
                                    onClick={() => updateAnswer('billingVolume', option as any)}
                                >
                                    {option}
                                </OptionButton>
                            ))}
                        </OptionsList>
                    )
                };
            case 2:
                return {
                    title: 'Em quais países você vende?',
                    content: (
                        <OptionsList>
                            {['Brasil', 'LATAM', 'Global'].map((option) => (
                                <OptionButton
                                    key={option}
                                    selected={answers.region === option}
                                    onClick={() => updateAnswer('region', option as any)}
                                >
                                    {option}
                                </OptionButton>
                            ))}
                        </OptionsList>
                    )
                };
            case 3:
                return {
                    title: 'Qual plataforma você utiliza atualmente?',
                    content: (
                        <OptionsList>
                            {['Hotmart', 'PerfectPay', 'Braip', 'Kiwify', 'Kirvano', 'ClickBank', 'Digistore', 'Yampi', 'Cartpanda', 'Stripe', 'Outros'].map((option) => (
                                <OptionButton
                                    key={option}
                                    selected={answers.platform === option}
                                    onClick={() => updateAnswer('platform', option as any)}
                                >
                                    {option}
                                </OptionButton>
                            ))}
                        </OptionsList>
                    )
                };
            case 4:
                return {
                    title: 'Precisa de integração com alguma plataforma específica?',
                    content: (
                        <InputField
                            type="text"
                            value={answers.integration}
                            onChange={(e) => updateAnswer('integration', e.target.value)}
                            placeholder="Digite aqui (opcional)"
                        />
                    )
                };
            case 5:
                return {
                    title: 'Qual o seu e-mail para receber as recomendações?',
                    content: (
                        <InputField
                            type="email"
                            value={answers.email}
                            onChange={(e) => {
                                updateAnswer('email', e.target.value);

                                // Valida o email somente se o campo não estiver vazio
                                if (e.target.value && !isValidEmail(e.target.value)) {
                                    setError('Por favor, informe um email válido.');
                                } else {
                                    setError('');
                                }
                            }}
                            onBlur={() => {
                                // Valida novamente ao perder o foco
                                if (answers.email && !isValidEmail(answers.email)) {
                                    setError('Por favor, informe um email válido.');
                                }
                            }}
                            placeholder="seu@email.com"
                            required
                        />
                    )
                };
            case 6:
                return {
                    title: 'Qual o seu telefone para contato?',
                    content: (
                        <InputField
                            type="tel"
                            value={answers.phone}
                            onChange={handlePhoneChange}
                            placeholder="(00) 00000-0000"
                            required
                            maxLength={16} // (00) 00000-0000 tem 15 chars, +1 para segurança
                        />
                    )
                };
            default:
                return { title: '', content: null };
        }
    };

    // Limpar mensagens de erro quando mudar de pergunta
    useEffect(() => {
        setError('');
        setIsSubmitting(false);
    }, [currentQuestionId]);

    const handleSubmit = () => {
        // Evitar múltiplos cliques
        if (isSubmitting) return;

        // Validação para cada tipo de pergunta
        let canAdvance = true;

        switch (currentQuestionId) {
            case 1:
                if (!answers.billingVolume) {
                    setError('Por favor, selecione um volume de faturamento para continuar.');
                    canAdvance = false;
                }
                break;
            case 2:
                if (!answers.region) {
                    setError('Por favor, selecione uma região para continuar.');
                    canAdvance = false;
                }
                break;
            case 3:
                if (!answers.platform) {
                    setError('Por favor, selecione uma plataforma para continuar.');
                    canAdvance = false;
                }
                break;
            case 4:
                // A questão 4 é opcional, sempre pode avançar
                break;
            case 5:
                if (!answers.email) {
                    setError('Por favor, informe seu email para continuar.');
                    canAdvance = false;
                } else if (!isValidEmail(answers.email)) {
                    setError('Por favor, informe um email válido para continuar.');
                    canAdvance = false;
                }
                break;
            case 6:
                if (!answers.phone) {
                    setError('Por favor, informe seu telefone para continuar.');
                    canAdvance = false;
                } else if (!isValidPhone(answers.phone)) {
                    setError('Por favor, informe um telefone válido para continuar.');
                    canAdvance = false;
                }
                break;
        }

        if (!canAdvance) return;

        setError('');
        setIsSubmitting(true);

        if (isLastQuestion) {
            // Envia evento do Facebook antes de navegar
            sendFacebookEvent()
                .then(() => {
                    // Navega para a página de carregamento
                    navigate('/loading');
                })
                .catch(() => {
                    // Navega mesmo se houver erro
                    navigate('/loading');
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        } else {
            // Adiciona um pequeno atraso para permitir a animação visual
            setTimeout(() => {
                // Avança para a próxima pergunta
                goToNextQuestion();
                navigate(`/question/${currentQuestionId + 1}`);
                setIsSubmitting(false);
            }, 300);
        }
    };

    const handleBack = () => {
        if (currentQuestionId > 1) {
            goToPreviousQuestion();
            navigate(`/question/${currentQuestionId - 1}`);
        } else {
            navigate('/');
        }
    };

    const { title, content } = getQuestionContent();

    return (
        <QuestionContainer>
            {isSubmitting && (
                <LoadingOverlay>
                    <LoadingSpinner />
                </LoadingOverlay>
            )}
            <QuestionCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <StepIndicator>
                    {[1, 2, 3, 4, 5, 6].map((step) => (
                        <Step key={step} active={step <= currentQuestionId} />
                    ))}
                </StepIndicator>
                <QuestionTitle>{title}</QuestionTitle>
                {content}
                {error && <ErrorMessage>{error}</ErrorMessage>}
                <ButtonContainer>
                    <Button
                        onClick={handleBack}
                        fullWidth={isMobile}
                        disabled={isSubmitting}
                    >
                        {currentQuestionId === 1 ? 'Voltar ao início' : 'Anterior'}
                    </Button>
                    <Button
                        primary
                        onClick={handleSubmit}
                        fullWidth={isMobile}
                        disabled={isSubmitting}
                    >
                        {isLastQuestion ? 'Compare Agora' : 'Avançar'}
                    </Button>
                </ButtonContainer>
            </QuestionCard>
        </QuestionContainer>
    );
};

export default Question;